// freeze.ts — Which result to display: the current computation, or the snapshot frozen at validation?
//
// This is THE promise made to the sales reps (book §20): the price of a VALIDATED or SENT quote never moves,
// whatever happens to the catalogue afterwards. A draft follows the current catalogue — the other half of the
// same promise: what is being prepared reflects today's prices.
//
// Why the rule lives in the ENGINE and not in the screen: it is a business rule, not a display concern — and
// the engine is where it is TESTABLE. As long as it lived implicitly in a page-level memo, nothing prevented a
// refactor from bypassing it.
//
// HISTORY WORTH KNOWING: the frozen snapshot was written at validation from the very start but never read
// back — the screen always recomputed. It went unnoticed because the engine read a frozen embedded catalogue,
// so recomputing a validated quote necessarily gave the same figures. Injecting the catalogue (prices can now
// change) made the gap bite overnight.

import type { QuoteState, QuoteResult, ComputeOptions } from "./compute";
import { compute } from "./compute";
import type { VolumeGrid } from "../pricing/volume-tiers";

/**
 * Quote lifecycle statuses (book §20): `draft` (being prepared), `pending` (awaiting validation), `validated`,
 * `refused` (by the supervisor), `to_fix` (sent back for correction) and `sent` (delivered to the client).
 * No persisted value of this union exists yet: the quote store is built from scratch for the Nexus port.
 */
export type QuoteStatus = "draft" | "pending" | "validated" | "refused" | "to_fix" | "sent";

/**
 * Statuses whose result is FROZEN: the quote reached the client, its price is a commitment.
 * `pending`, `refused` and `to_fix` are not — nothing has been promised yet, and the quote is meant to go
 * back into correction: it must follow the current catalogue.
 */
export const FROZEN_STATUSES: readonly QuoteStatus[] = ["validated", "sent"];

/**
 * Is this quote's result frozen (recomputation forbidden)?
 * @param status Lifecycle status of the quote
 * @returns True when the status freezes the result
 */
export const isFrozen = (status: QuoteStatus): boolean => FROZEN_STATUSES.includes(status);

/** Where the displayed result comes from. */
export type ResultSource =
  | "computed" // recomputed against the current catalogue (drafts & co)
  | "frozen" // validation snapshot, verbatim
  | "frozen_missing"; // ANOMALY: frozen status but no snapshot — we still do not recompute

/** Result to display for a quote, with its origin. */
export interface DisplayedResult {
  /** Result to display. null on `frozen_missing` (show nothing rather than a wrong price). */
  result: QuoteResult | null;
  source: ResultSource;
}

/**
 * VIEWABLE FOR LIFE — a freeze written by an EARLIER model may lack the fields that appeared since
 * (services, flatFeeLines, marginCoverage…; the first production freezes carried only `totalFinal`). The ABSENT
 * fields are completed with neutral display values, NEVER touching a present field: verbatim stays verbatim,
 * and screens need not guard field by field.
 *
 * Scope: this only bridges snapshots written by earlier versions of THIS engine model. Snapshots exported by
 * the standalone quote-tool (French result keys) are deliberately NOT migrated — the quote store starts from
 * scratch in Nexus, so no such snapshot is ever loaded here.
 * @param frozen Result snapshot read from storage
 * @returns The same object when it is already complete, else a copy completed with neutral defaults
 */
export function hydrateLegacyFreeze(frozen: QuoteResult): QuoteResult {
  const defaults: QuoteResult = {
    lines: [],
    materials: {},
    flatFeeLines: [],
    totalPrint: 0,
    totalPvPublic: 0,
    totalBeforeDiscount: 0,
    generalDiscountAmountVal: 0,
    totalFinal: 0,
    totalTargetPa: 0,
    marginCoverage: { realLineCount: 0, printLineCount: 0, actualNetPv: 0, actualPa: 0, weightedTargetMarginFrac: null, paTotal: 0, breakdown: { negotiated: { n: 0, pa: 0 }, actual: { n: 0, pa: 0 }, target: { n: 0, pa: 0 } } },
    rush: { amount: 0, label: "", pa: 0 },
    blockers: [],
    serviceItem: { lines: [], servicesTotal: 0, servicesPaTotal: 0, alerts: [] },
    ancillaryCosts: { lines: [], total: 0, blockers: [] }, // freezes older than book 4.9: no extra fees
    fileCount: 0,
    elementCount: 0,
    totalM2: 0,
    totalWeight: 0,
  };
  // Already complete (the common case): returned as-is, same object identity.
  for (const k of Object.keys(defaults) as (keyof QuoteResult)[]) {
    if (!(k in frozen)) return { ...defaults, ...frozen };
  }
  return frozen;
}

/**
 * THE result to display for a quote, according to its status.
 *
 * - Frozen status (validated / sent) + snapshot: the SNAPSHOT, verbatim (completed with neutral values if the
 *   freeze predates the current model). NO call to compute() at all: the current catalogue must not even be
 *   consulted, otherwise the promise holds only as far as a test does.
 * - Frozen status WITHOUT snapshot: `frozen_missing` and a null result. The database forbids it (the snapshot
 *   is required at validation), so it is corruption. We refuse to recompute: showing recomputed prices as if
 *   they were those of the quote sent to the client would be the worst of both worlds. The screen must say so.
 * - Any other status: computed against the current catalogue (drafts follow today's prices).
 * @param status Lifecycle status of the quote
 * @param state Quote state to compute when the status is not frozen
 * @param grid Volume rate table injected into the computation
 * @param frozen Result snapshot stored at validation, if any
 * @param opts Compute options forwarded to compute()
 * @returns The result to display and where it comes from
 */
export function displayedResult(
  status: QuoteStatus,
  state: QuoteState,
  grid: VolumeGrid,
  frozen: QuoteResult | null | undefined,
  opts: ComputeOptions = {},
): DisplayedResult {
  if (isFrozen(status)) {
    if (!frozen) return { result: null, source: "frozen_missing" };
    return { result: hydrateLegacyFreeze(frozen), source: "frozen" };
  }
  return { result: compute(state, grid, opts), source: "computed" };
}
